/* eslint-disable react/require-default-props */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { Close } from '@material-ui/icons';
import {
  TemplateIcon,
  AttachFileIcon,
  SMSIcon,
  WhatsappIcon,
} from '~/assets/svgIcons';
import * as lib from '~/dependencies';
import { colors } from '~/layout';
import settings from '~/assets/settings.json';
import { ChannelTypeEnum } from '~/enums/conversations';
import { DraftMessage } from '~/interfaces/conversations';
import { SmsTemplate, SmsSender } from '~/types/accountInfo';
import { RootState } from '~/store/config';
import {
  checkGSMAlphabet,
  countLineBreaks,
  isMmsSupportedNumber,
  eventTracker,
  isUndefined,
  log,
} from '~/helpers';
import Templates from '../Templates';
import TemplateParamsEditor from '../TemplateParamsDialog';
import FilePickerPreview from '../FilePickerPreview';
import SendChannelButton from '../SendChannelButton';
import FileUploadProgressModal, {
  FileUploadResult,
} from '../FilePickerPreview/FileUploadProgressModal';
import {
  AlertSeverity,
  AlertBanner,
  AlertBannerMessage,
  Bold,
  Button,
  ClearTemplateButton,
  Container,
  Input,
  Row,
  TemplateBanner,
  TemplateBannerLabel,
  Actions,
} from './styles';

/**
 * Inserts text at the current cursor location of a textarea or input element.
 * @param inputRef Reference to the input or textarea element
 * @param text Text to be inserted
 */
// function insertTextAtCursorLocation(inputRef: any, text: string) {
//   const start = inputRef.selectionStart;
//   const end = inputRef.selectionEnd;

//   inputRef.value =
//     inputRef.value.slice(0, start) + text + inputRef.value.slice(end);

//   // Move cursor after inserted text
//   const newPosition = start + text.length;
//   inputRef.selectionStart = newPosition;
//   inputRef.selectionEnd = newPosition;
// }

type MessageTextInputProps = {
  onSend: (draftMessage: DraftMessage) => void;
  /**
  }) => void;
  /**
   * Emitted when the user add a template with usage '10dlc'
   * @param template returns the selected template
   */
  onTemplateAppend?: (template: SmsTemplate) => void;
  onTemplateRemoved?: () => void;
  channelType: ChannelTypeEnum;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  placeholderFontSize?: string;
  enforceTemplateRequired?: boolean;
  hasNoReceipientsOrSender?: boolean;
  sender?: SmsSender | null;
  /**
   * When true, forces the file picker (attachment button + drop zone) to be hidden,
   * regardless of channel/message state.
   */
  disableMediaPicker?: boolean;
  /**
   * Optional list of channels to show in the channel selector dropdown.
   */
  channelOptions: ChannelTypeEnum[];
  onChannelChange: (channel: ChannelTypeEnum) => void;
  destNumbers: string[];
  isNewConversation?: boolean;
};

// Matches anything between {{}}
const TEMPLATE_PARAMS_REGEX = /\{\{.+?\}\}/;

function addTemplateHighlight(text: string) {
  return text.replace(/(\{\{.+?\}\})/g, '<span class="highlight">$1</span>'); // /g needed here to replace all occurrences
}

function cleanTemplateHighlight(element: HTMLDivElement | null) {
  return (
    element?.innerHTML
      ?.replace(/<span class="highlight">/g, '')
      ?.replace(/<\/span>/g, '')
      ?.replace(/<br>/g, '') ?? ''
  );
}

/**
 * Checks whether tempalte message contains parameters based on the TEMPLATE_PARAMS_REGEX pattern.
 */
function hasTemplateVariables(templateMessage: string = ''): boolean {
  return TEMPLATE_PARAMS_REGEX.test(templateMessage);
}

function channelOptionsMapper(types: ChannelTypeEnum[]) {
  return types
    .map(type => {
      const channelOptionMap: Partial<Record<
        ChannelTypeEnum,
        { type: ChannelTypeEnum; label: string; icon: string; color: string }
      >> = {
        [ChannelTypeEnum.WHATSAPP]: {
          type: ChannelTypeEnum.WHATSAPP,
          label: 'WhatsApp',
          icon: WhatsappIcon,
          color: colors.green08,
        },
        [ChannelTypeEnum.SMS]: {
          type: ChannelTypeEnum.SMS,
          label: 'SMS',
          icon: SMSIcon,
          color: colors.lightBlue12,
        },
      };
      return channelOptionMap[type];
    })
    .filter((opt): opt is NonNullable<typeof opt> => Boolean(opt));
}

export default function TextInput({
  onSend,
  onTemplateAppend,
  onTemplateRemoved,
  channelType,
  disabled,
  isLoading,
  placeholder,
  placeholderFontSize,
  enforceTemplateRequired,
  hasNoReceipientsOrSender,
  sender,
  disableMediaPicker,
  channelOptions,
  onChannelChange,
  destNumbers,
  isNewConversation = false,
}: MessageTextInputProps) {
  // REF's
  const textareaRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef(null);

  // HOOK's
  const { t } = lib.useTranslation();
  const { isMmsEnabled } = useSelector(
    (state: RootState) => state.accountInfo.featureFlags
  );

  // STATE's
  const [isMaxLengthExceeded, setIsMaxLengthExceeded] = useState<boolean>(
    false
  );
  const [invalidChars, setInvalidChars] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [template, setTemplate] = useState<SmsTemplate | null>(null);
  const [templateParams, setTemplateParams] = useState(undefined);
  const [files, setFiles] = useState<File[]>([]);
  const [openTemplateList, setOpenTemplateList] = useState<boolean>(false);
  const [openTemplateParamsEditor, setOpenTemplateParamsEditor] = useState<
    boolean
  >(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState<FileUploadResult[]>([]);

  // CONST's
  const isWhatsAppChannel = channelType === ChannelTypeEnum.WHATSAPP;
  const isSmsOrMmsChannel =
    channelType === ChannelTypeEnum.SMS || channelType === ChannelTypeEnum.MMS;
  const hasWhatsAppTemplate = template?.usage === 'whatsapp';

  const hasMedias = Boolean(files?.length);

  const hasInvalidChars = invalidChars.length > 0;
  const invalidCharsText = invalidChars.join(', ');

  const isEveryDestNumbersMmsSupported = destNumbers.every(
    isMmsSupportedNumber
  );

  const mmsSupportedCount = destNumbers.filter(isMmsSupportedNumber).length;
  const mmsUnsupportedCount = destNumbers.length - mmsSupportedCount;

  /* eslint-disable no-nested-ternary */
  const alertSeverity: AlertSeverity | null =
    isMaxLengthExceeded || hasInvalidChars
      ? 'error'
      : isSmsOrMmsChannel && hasMedias && !isEveryDestNumbersMmsSupported
      ? 'warning'
      : null;

  const hasBlockingError = alertSeverity === 'error';

  const hasTemplateParams = hasTemplateVariables(template?.messageText);
  const isTemplateRequired =
    isWhatsAppChannel && !template && enforceTemplateRequired;

  // If a WA template is selected, and it has parameters, but the user hasn't filled them yet, we should disable the send button
  const isTemplateNotReady = Boolean(
    isWhatsAppChannel && template && hasTemplateParams && !templateParams
  );

  const isPlaceholderLocked = Boolean(placeholder);

  const isInputDisabled = Boolean(disabled);
  const isMediaPickerDisabled =
    Boolean(disableMediaPicker) || (isWhatsAppChannel && isNewConversation);

  // Input is read-only when a WA template is active, or a file is attached on WA
  const isInputReadOnly =
    isInputDisabled ||
    hasWhatsAppTemplate ||
    (isWhatsAppChannel && hasMedias) ||
    (isWhatsAppChannel && isNewConversation);

  const hasNoContent = !files?.length && !message?.length;

  const hasUnapprovedMedia =
    isSmsOrMmsChannel &&
    (files?.length ?? 0) > 0 &&
    uploadResults?.length !== files.length;

  const isSendDisabled =
    isLoading ||
    isInputDisabled ||
    hasBlockingError ||
    hasNoContent ||
    hasUnapprovedMedia ||
    isTemplateNotReady ||
    (isNewConversation && isUndefined(sender));

  const filePickerSettings = isWhatsAppChannel
    ? settings.whatsapp
    : settings.mms;

  const MAX_MSG_LNG = filePickerSettings.maxMsgLength;

  /* eslint-disable no-nested-ternary */
  const showFilePicker = isWhatsAppChannel
    ? message?.length === 0
    : isMmsEnabled &&
      isSmsOrMmsChannel &&
      destNumbers.some(isMmsSupportedNumber);

  // On WhatsApp `showFilePicker` flips on the first typed character. Keep the input
  // inside FilePickerPreview anyway (with the dropzone disabled) so React does not
  // remount the contentEditable, which would drop focus and the caret.
  const wrapInFilePicker = showFilePicker || isWhatsAppChannel;

  const replaceSmsWithMmsLabel = (
    channels: ReturnType<typeof channelOptionsMapper>
  ) => {
    if (!isMmsEnabled) return channels;

    return channels.map(channel =>
      channel.type === ChannelTypeEnum.SMS
        ? { ...channel, label: 'SMS/MMS' }
        : channel
    );
  };

  const validateSMSMessage = (msg: string) => {
    const invalidCharsList = checkGSMAlphabet(msg);

    if (invalidCharsList.length > 0) {
      setInvalidChars([...invalidCharsList]);
    } else {
      setInvalidChars([]);
    }

    setIsMaxLengthExceeded(msg?.length > MAX_MSG_LNG);

    setMessage(msg);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateWhatsAppMessage = (msg: string) => {
    const invalidCharsList = checkGSMAlphabet(msg);

    if (invalidCharsList.length > 0) {
      setInvalidChars([...invalidCharsList]);
    } else {
      setInvalidChars([]);
    }

    setIsMaxLengthExceeded(msg?.length > MAX_MSG_LNG);

    setMessage(msg);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validateMmsMessage = (msg: string) => {
    setMessage(msg);
  };

  const validatorFactory = (type: ChannelTypeEnum) => {
    switch (type) {
      case ChannelTypeEnum.SMS:
        return { validate: (msg: string) => validateSMSMessage(msg) };
      case ChannelTypeEnum.WHATSAPP:
        return { validate: (msg: string) => validateWhatsAppMessage(msg) };
      case ChannelTypeEnum.MMS:
        return { validate: (msg: string) => validateMmsMessage(msg) };
      default:
        return { validate: (msg: string) => validateSMSMessage(msg) };
    }
  };

  const validator = validatorFactory(channelType);

  // Keep the latest message reachable from the callback ref below.
  const messageRef = useRef<string>(message);
  messageRef.current = message;

  // The input is a contentEditable whose DOM content is managed imperatively, so
  // when React remounts it (e.g. `showFilePicker` toggles and the FilePickerPreview
  // wrapper is added/removed after recipients change) the typed/template text is lost.
  // Restore it from state whenever a new node is attached.
  const setTextareaRef = useCallback((node: HTMLDivElement | null) => {
    if (node && node !== textareaRef.current && messageRef.current) {
      node.innerHTML = addTemplateHighlight(messageRef.current);
    }
    textareaRef.current = node;
  }, []);

  // Sync textarea innerHTML when a template is selected.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !template) return;
    textarea.innerHTML = addTemplateHighlight(message);
  }, [template]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    if (isInputDisabled) return;

    const textarea = textareaRef.current;
    const plainText = cleanTemplateHighlight(e.currentTarget);

    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;

    validator.validate(plainText);
  };

  const handleOnTemplateChange = (activeTemplate: SmsTemplate) => {
    const textarea = textareaRef.current;

    if (textarea) {
      const { messageText: templateMessage } = activeTemplate;

      if (onTemplateAppend) {
        onTemplateAppend(activeTemplate);
      }

      // Line height based on current font size font-size: 16px;
      const fontLineHeight = 22.5;
      const updatedMessage = isWhatsAppChannel
        ? templateMessage
        : `${message} ${templateMessage}`;
      const lineCount = countLineBreaks(updatedMessage);

      // @ts-ignore
      textarea.style.height = 'auto';
      // @ts-ignore
      textarea.style.height = `${lineCount * fontLineHeight}px`;

      // insertTextAtCursorLocation(textarea, templateMessage);

      flushSync(() => {
        setOpenTemplateList(false);
        setTemplate(activeTemplate);
        setIsMaxLengthExceeded(updatedMessage.length > MAX_MSG_LNG);
        setMessage(updatedMessage);
      });
      // textarea.innerHTML is set reactively by useLayoutEffect above
    }
  };

  const afterSentCleanup = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.textContent = '';
    textarea.style.height = 'auto';
    setMessage('');
    setIsMaxLengthExceeded(false);
    setInvalidChars([]);
    setTemplate(null);
    setTemplateParams(undefined);
    setFiles([]);
    setUploadResults([]);
  }, []);

  useEffect(() => {
    afterSentCleanup();
  }, [channelType, afterSentCleanup]);

  // Clear the input if message is just a <br> tag, which can happen when user deletes all text in a contentEditable div
  useEffect(() => {
    if (message === '<br>') {
      setMessage('');
    }
  }, [message, setMessage]);

  function sendNewMessage() {
    // @ts-ignore
    const { textContent } = textareaRef.current;

    if (
      onSend &&
      (textContent.length > 0 || template?.id || files?.length > 0)
    ) {
      const payload = {
        channelType,
        message: String(textContent),
        ...(isWhatsAppChannel && {
          templateId: template?.id,
          templateParams,
          // Whatsapp supports single file only
          ...(files?.length > 0 && { file: files[0] }),
        }),
        ...(uploadResults?.length && {
          mediaIds: uploadResults.map(result => result.uploadResult.mediaId),
        }),
      };
      onSend(payload);

      afterSentCleanup();
      // @ts-ignore
      eventTracker(events => events.SendSMSClicked);
    }
  }

  const handleClearTemplate = () => {
    afterSentCleanup();

    if (onTemplateRemoved) onTemplateRemoved();
  };

  const handleSendClick = () => {
    if (isSendDisabled) return;
    // const textarea = textareaRef.current;
    // if (!textarea) return;

    switch (channelType) {
      case ChannelTypeEnum.WHATSAPP:
        if (hasWhatsAppTemplate && hasTemplateParams && !templateParams) {
          setOpenTemplateParamsEditor(true);
        } else {
          sendNewMessage();
        }
        break;
      default:
        // @ts-ignore
        sendNewMessage();
        break;
    }
  };

  /**
   * WhatsApp template: If Whatsapp template is selected, input can be clicked regardless of disabled
   */
  const handleInputMessageClick = () => {
    if (isInputDisabled) return;
    if (isWhatsAppChannel && hasTemplateParams) {
      setOpenTemplateParamsEditor(true);
    }
  };

  /**
   * Allow opening templates if:
   * It's a WhatsApp channel (enabled even when disabled), OR
   * The input is not disabled
   */
  const toggleTemplateList = () => {
    switch (channelType) {
      case ChannelTypeEnum.WHATSAPP:
        if (hasMedias) return;

        if (!hasNoReceipientsOrSender) {
          setOpenTemplateList(!openTemplateList);
        }
        break;
      default:
        setOpenTemplateList(!openTemplateList);
        break;
    }
  };

  const toggleMediaPicker = () => {
    if (isInputDisabled || isMediaPickerDisabled) return;

    // @ts-ignore
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleDropAccepted = (acceptedFiles: File[] | undefined) => {
    if (acceptedFiles === undefined) return;
    setFiles(prev => [...prev, ...acceptedFiles]);
    setUploadModalOpen(true);
  };

  const handleFileRemove = (file: File) => {
    setFiles(prev => prev.filter(f => f !== file));

    // Workaroud as whatsapp does not use the uploadResults state to send the file, so we need to remove the file from the uploadResults state when the user removes it from the input
    if (uploadResults?.length > 0) {
      setUploadResults(prev => prev.filter(result => result.file !== file));
    }
  };

  const handleFileUpload = (results: FileUploadResult[]) => {
    setUploadResults(results);
    setUploadModalOpen(false);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  const renderInputMessage = (
    <Input
      // @ts-ignore
      onPaste={event => {
        if (event?.clipboardData?.getData('text')) return;
        event.preventDefault();
        lib.ToastMessage(
          'warn',
          'Paste file not allowed. Please use the attachment button to send files.'
        );
      }}
      id="input-message"
      data-test-id="input-message"
      ref={setTextareaRef}
      style={
        {
          '--placeholder-font-size': placeholderFontSize ?? '16px',
        } as React.CSSProperties
      }
      {...(!hasMedias && {
        'data-placeholder':
          placeholder ?? t('screens.conversation.txtInputText'),
      })}
      onInput={handleInput}
      aria-disabled={isPlaceholderLocked || isInputDisabled}
      contentEditable={isPlaceholderLocked ? false : !isInputReadOnly}
      {...(hasWhatsAppTemplate && {
        isSpecialTemplate: true,
        onClick: handleInputMessageClick,
      })}
    />
  );

  const componentInfoLogger = () => {
    log.debug('Input Message Info: ', {
      channelType,
      isNewConversation,
      template,
      sender,
      hasNoReceipientsOrSender,
      isLoading,
      isInputDisabled,
      hasBlockingError,
      hasNoContent,
      hasUnapprovedMedia,
      isTemplateNotReady,
      isInputReadOnly,
      hasWhatsAppTemplate,
      isMediaPickerDisabled,
      isMaxLengthExceeded,
      hasInvalidChars,
      destNumbers,
    });
  };

  return (
    <Container
      onDoubleClick={componentInfoLogger}
      severity={alertSeverity ?? undefined}
      className="message-text-input-container">
      {alertSeverity ? (
        <AlertBanner severity={alertSeverity}>
          {isMaxLengthExceeded && (
            <AlertBannerMessage>
              {t('screens.conversation.smsMaxCharLng')}{' '}
              <Bold>
                {message.length}/{MAX_MSG_LNG}
              </Bold>
              .
            </AlertBannerMessage>
          )}
          {hasInvalidChars && (
            <AlertBannerMessage>
              {t('screens.conversation.smsInvalidChars')}{' '}
              <Bold>{invalidCharsText}</Bold>
            </AlertBannerMessage>
          )}

          {!isEveryDestNumbersMmsSupported && (
            <AlertBannerMessage>
              {`Attachments will be delivered as MMS to ${mmsSupportedCount} recipient${
                mmsSupportedCount !== 1 ? 's' : ''
              } (US/CA/AU). ${mmsUnsupportedCount} recipient${
                mmsUnsupportedCount !== 1 ? 's' : ''
              } in other countries will receive the text only.`}
            </AlertBannerMessage>
          )}
        </AlertBanner>
      ) : null}

      {openTemplateParamsEditor ? (
        <TemplateParamsEditor
          template={template!}
          onClose={() => setOpenTemplateParamsEditor(!openTemplateParamsEditor)}
          onParamsFilled={(paramValues: any, processedMessage: string) => {
            setTemplateParams(paramValues);
            // @ts-ignore
            textareaRef.current.textContent = processedMessage;
          }}
        />
      ) : null}

      {template ? (
        <TemplateBanner>
          <TemplateBannerLabel>
            {t('screens.conversation.template')}:{' '}
            <b title={template.name}>{template.name}</b>
          </TemplateBannerLabel>
          <ClearTemplateButton
            type="button"
            data-test-id="clear-template"
            title={t('screens.conversation.clearTemplate')}
            onClick={handleClearTemplate}>
            <Close fontSize="small" />
          </ClearTemplateButton>
        </TemplateBanner>
      ) : null}

      <Row>
        <Actions>
          {showFilePicker && (
            <Button
              title="File picker"
              aria-label="File picker"
              data-test-id="file-picker-button"
              src={AttachFileIcon}
              active={Boolean(files?.length)}
              disabled={isMediaPickerDisabled}
              onClick={toggleMediaPicker}
            />
          )}

          <Button
            title="Templates"
            aria-label="Open template selector"
            data-test-id="template-selector"
            src={TemplateIcon}
            onClick={toggleTemplateList}
            highlight={Boolean(isTemplateRequired)}
            active={Boolean(template)}
          />
        </Actions>

        <div className="input-wrapper">
          {wrapInFilePicker ? (
            <FilePickerPreview
              disabled={!showFilePicker}
              files={files}
              maxFiles={filePickerSettings.maxFiles}
              maxFileSize={filePickerSettings.maxFileSize}
              acceptTypes={filePickerSettings.acceptTypes}
              allowedFileExtensions={filePickerSettings.allowedFileExtensions}
              inputRef={fileInputRef}
              onDropAccepted={handleDropAccepted}
              onRemove={handleFileRemove}
              onThumbClick={() => setUploadModalOpen(true)}>
              {renderInputMessage}
            </FilePickerPreview>
          ) : (
            renderInputMessage
          )}
        </div>

        <SendChannelButton
          data-test-id="send-message"
          onSend={handleSendClick}
          disabled={isSendDisabled}
          isLoading={isLoading}
          onChannelChange={onChannelChange}
          channelOptions={replaceSmsWithMmsLabel(
            channelOptionsMapper(channelOptions)
          )}
        />
      </Row>
      {openTemplateList ? (
        <Templates
          channelType={channelType}
          onClose={toggleTemplateList}
          onTemplateChange={handleOnTemplateChange}
        />
      ) : null}

      {isSmsOrMmsChannel && (
        <FileUploadProgressModal
          open={uploadModalOpen}
          files={files}
          onRemove={handleFileRemove}
          onClose={handleCloseUploadModal}
          onComplete={handleFileUpload}
        />
      )}
    </Container>
  );
}
